







































































































import { Component, Vue, Watch } from "vue-property-decorator";
import { Getter } from "vuex-class";
import { Form, User, Department } from "../../../interfaces";
import FormCard from "@/components/FormCard.vue";
import LineChart from "@/components/LineChart.vue";
import SpinnerIcon from "../../../assets/icons/SpinnerIcon.vue";
import { median } from "../../../utils";
import DatePicker from "@/components/DatePicker.vue";
import ExpandableCard from "@/components/ExpandableCard.vue";
import ComboBox from "@/components/ComboBox.vue";

interface DataEntry {
  user: User;
  value: number;
  date: string;
}
interface PulseData {
  [chartn: string]: Array<DataEntry>;
}

const MILISECONDS_IN_A_DAY = 1000 * 60 * 60 * 24;

@Component({
  components: {
    appFormCard: FormCard,
    appLineChart: LineChart,
    appSpinnerIcon: SpinnerIcon,
    appDatePicker: DatePicker,
    appExpandableCard: ExpandableCard,
    appComboBox: ComboBox
  }
})
export default class Pulse extends Vue {
  @Getter("pulseForms", { namespace: "form" }) forms!: Array<Form>;
  @Getter("user", { namespace: "auth" }) user!: User;
  @Getter("userIsInMultipleDepartments", { namespace: "auth" })
  userIsInMultipleDepartments!: boolean;
  @Getter("departments") departments!: Department[];

  pulseData: PulseData | null = null;

  chartTitles = [
    "O quanto estamos seguindo nossos valores",
    "Volume de trabalho",
    "Satisfação com as tarefas alocado",
    "Satisfação com a liderança direta",
    "Integração do núcleo",
    "Aptidão nas tarefas alocado"
  ];

  fromDate: string = new Date(2020, 0, 1).toISOString();
  toDate: string = new Date(
    new Date().getTime() + MILISECONDS_IN_A_DAY
  ).toISOString();

  selectedDepartment: { id: string; value: string } | null = null;
  departmentOptions: { id: string; value: string }[] = [];

  @Watch("selectedDepartment", { deep: true })
  onSelectedDepartmentChange() {
    this.axios
      .get(`/api/results/pulse/${this.selectedDepartment!.id}`)
      .then(({ data }) => (this.pulseData = data.result))
      .catch(error => console.log(error));
  }

  mounted() {
    this.departmentOptions = this.departments
      .map(({ _id, name }) => ({ id: _id, value: name }))
      .filter(department => this.user.department.includes(department.id));

    this.selectedDepartment =
      this.departmentOptions.find(
        department => department.id === this.user.department[0]
      ) ?? null;

    if (this.departmentOptions.length > 0) {
      this.selectedDepartment =
        this.departmentOptions.find(
          department => department.id === this.user.department[0]
        ) ?? null;
    }
  }

  get filteredData(): PulseData | null {
    if (!this.pulseData) return null;

    const filteredData: PulseData = {};
    Object.keys(this.pulseData).forEach(chart => {
      const chartData: DataEntry[] = this.pulseData![chart].filter(
        entry =>
          Date.parse(entry.date) >= Date.parse(this.fromDate) &&
          Date.parse(entry.date) <= Date.parse(this.toDate)
      );
      filteredData[chart] = chartData;
    });
    return filteredData;
  }

  get labels(): Array<string> {
    if (!this.filteredData) return [];
    const labels = this.filteredData.chart2.map(entry => entry.date);
    const uniqueLabels = Array.from(new Set(labels)).sort();
    return uniqueLabels;
  }

  get users(): Array<User> {
    if (!this.filteredData) return [];
    const users = this.filteredData.chart2.map(entry => entry.user);
    const uniqueUsers = Array.from(new Set(users));
    return uniqueUsers;
  }

  get preProcessedData(): PulseData | null {
    if (!this.filteredData) return null;
    const chart1: Array<DataEntry> = [];
    const chart6: Array<DataEntry> = [];

    this.labels.forEach(label => {
      this.users.forEach(user => {
        const userData = this.filteredData!.chart1.filter(
          entry => entry.user?._id === user?._id
        ).filter(entry => entry.date === label);
        const values = userData.map(entry => entry.value);
        const valuesMedian = median(values);
        if (!isNaN(valuesMedian)) {
          chart1.push({
            user: user,
            date: label,
            value: valuesMedian
          });
        }
      });
    });

    this.labels.forEach(label => {
      this.users.forEach(user => {
        const userData = this.filteredData!.chart6.filter(
          entry => entry.user?._id === user?._id
        ).filter(entry => entry.date === label);
        const values = userData.map(entry => entry.value);
        const valuesMedian = median(values);
        if (!isNaN(valuesMedian)) {
          chart6.push({
            user: user,
            date: label,
            value: valuesMedian
          });
        }
      });
    });
    return {
      chart1: chart1,
      chart2: this.filteredData.chart2,
      chart3: this.filteredData.chart3,
      chart4: this.filteredData.chart4,
      chart5: this.filteredData.chart5,
      chart6: chart6
    };
  }

  get chartData() {
    if (!this.preProcessedData) return [];
    return Object.keys(this.preProcessedData).map(chart => {
      const chartData: Array<DataEntry> = this.preProcessedData![chart];
      return {
        labels: this.labels.map(timestamp =>
          new Date(timestamp).toLocaleDateString("pt-BR", {
            month: "2-digit",
            year: "2-digit"
          })
        ),
        datasets: [
          {
            label: "Você",
            backgroundColor: "#D2D2D2",
            borderColor: "#D2D2D2",
            data: this.labels.map(label => {
              const entry = chartData.find(entry => {
                return (
                  entry.user?._id === this.user._id && entry.date === label
                );
              });
              return entry?.value ?? null;
            }),
            fill: false,
            spanGaps: true
          },
          {
            label: "Núcleo",
            backgroundColor: "#E83C2B",
            borderColor: "#E83C2B",
            data: this.labels.map(label => {
              const values = chartData
                .filter(entry => entry.date === label)
                .map(entry => entry.value);
              return median(values);
            }),
            fill: false,
            spanGaps: true
          }
        ]
      };
    });
  }
}
